import React, { useState, useRef, useEffect } from "react";
import { useSeo } from "./seo";
import { Plus, Minus } from "lucide-react";
import { DynamicNavigation, Footer } from "./components";
import CTA02 from "./components/CTA02Section";
import { useNavLinkClick } from "./hooks/useNavLinkClick";
import { useNavigate } from "react-router-dom";

const skilledVisaTypes = [
  {
    title: "Skilled Independent Visa",
    subclass: "Subclass 189",
    desc: "Points-based permanent residency visa for skilled workers with no employer or state sponsorship required.",
    url: "/australia-skilled-independent-visa-189",
  },
  {
    title: "Skilled Nominated Visa",
    subclass: "Subclass 190",
    desc: "Permanent residency visa for skilled workers nominated by an Australian state or territory government.",
    url: "/australia-skilled-nominated-visa-190",
  },
  {
    title: "Skilled Work Regional Visa",
    subclass: "Subclass 491",
    desc: "Temporary skilled work visa for those nominated by a state/territory or sponsored by an eligible family member in a regional area.",
    url: "/australia-skilled-work-regional-visa-491",
  },
  {
    title: "Permanent Residence Skilled Regional Visa",
    subclass: "Subclass 191",
    desc: "Permanent residency pathway for eligible holders of certain temporary regional visas who have lived and worked in regional Australia.",
    url: "/australia-skilled-regional-pr-visa-191",
  },
  {
    title: "Employer-Sponsored Skilled Visas",
    subclass: "",
    desc: "Visas for skilled workers sponsored by an approved Australian employer to fill positions where local workers are unavailable.",
    url: "/australia-employer-sponsored-visas",
  },
  {
    title: "Employer Nomination Scheme Visa",
    subclass: "Subclass 186",
    desc: "Permanent residency visa for skilled workers nominated by their Australian employer under the ENS program.",
    url: "/australia-ens-visa-186",
  },
  {
    title: "Temporary Skill Shortage Visa",
    subclass: "Subclass 482",
    desc: "Temporary visa allowing employers to address genuine skill shortages by bringing in qualified overseas workers.",
    url: "/australia-tss-visa-482",
  },
  {
    title: "Skilled Employer Sponsored Regional Visa",
    subclass: "Subclass 494",
    desc: "Temporary visa for skilled workers sponsored by an employer in regional Australia, with a pathway to permanent residence.",
    url: "/australia-regional-employer-sponsored-visa-494",
  },
];

const VisaCard = ({ title, subclass, desc, url }: { title: string; subclass: string; desc: string; url: string }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const divRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!divRef.current) return;
    const bounds = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
  };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      className="relative rounded-xl p-0.5 bg-white overflow-hidden shadow-md cursor-pointer w-full"
    >
      {visible && (
        <div
          className="pointer-events-none blur-xl bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 size-48 absolute z-0 transition-opacity duration-300"
          style={{ top: position.y - 96, left: position.x - 96 }}
        />
      )}
      <div className="relative z-10 bg-white p-6 h-full w-full rounded-[10px] flex flex-col">
        {subclass && (
          <span className="inline-block text-xs font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1 mb-3 w-max">
            {subclass}
          </span>
        )}
        <h3 className="text-base font-bold text-gray-800 mb-2 leading-snug">{title}</h3>
        <a
          href={url}
          className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-[#8A7DFF] px-5 py-2 rounded-full w-max hover:-translate-y-0.5 transition"
        >
          Know More
          <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.53 6.53a.75.75 0 0 0 0-1.06L7.757.697a.75.75 0 1 0-1.06 1.06L10.939 6l-4.242 4.243a.75.75 0 0 0 1.06 1.06zM0 6v.75h12v-1.5H0z" fill="#fff" />
          </svg>
        </a>
      </div>
    </div>
  );
};

const skillsRequirements = [
  "Applicants must be under 45 years of age at the time of invitation.",
  "Applicants must demonstrate competent English language proficiency (e.g., IELTS, PTE).",
  "A relevant educational qualification is required.",
  "Applicants must have skilled work experience in their nominated occupation.",
  "The nominated occupation must be listed on Australia's Skilled Occupation Lists.",
  "A positive skills assessment from the relevant assessing authority is mandatory.",
  "Applicants must meet the minimum points requirement (typically 65 points or higher).",
  "Applicants must meet health and character requirements set by the Australian Government."
];

const industries = [
  {
    label: "Healthcare",
    desc: "Nurses, doctors, and allied health professionals are among the most in-demand across Australia.",
    bg: "bg-violet-100",
    iconColor: "#7F22FE",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 4.667v18.666M4.667 14h18.666" stroke="#7F22FE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="2.333" y="2.333" width="23.334" height="23.334" rx="4" stroke="#7F22FE" strokeWidth="2"/>
      </svg>
    ),
    image: "/assets/skilled-visas-australia/in-demand-industries/healthcare.jpg",
  },
  {
    label: "Engineering",
    desc: "Civil, mechanical, and electrical engineers are critical to Australia's infrastructure growth.",
    bg: "bg-green-100",
    iconColor: "#00A63E",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.667 23.333L11.667 14l4.666 5.833L19.833 14l4.667 9.333" stroke="#00A63E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="14" cy="8.167" r="3.5" stroke="#00A63E" strokeWidth="2"/>
      </svg>
    ),
    image: "/assets/skilled-visas-australia/in-demand-industries/engineering.jpg",
  },
  {
    label: "Information Technology",
    desc: "Software engineers, data analysts, and cybersecurity experts are sought across all sectors.",
    bg: "bg-blue-100",
    iconColor: "#2563EB",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2.333" y="5.833" width="23.334" height="16.334" rx="2" stroke="#2563EB" strokeWidth="2"/>
        <path d="M9.333 11.667L7 14l2.333 2.333m9.334-4.666L21 14l-2.333 2.333M13.417 17.5l1.166-7" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    image: "/assets/skilled-visas-australia/in-demand-industries/it.jpg",
  },
  {
    label: "Construction",
    desc: "Project managers, architects, and tradespeople fuel Australia's booming construction industry.",
    bg: "bg-orange-100",
    iconColor: "#F54900",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.333 25.667h23.334M4.667 25.667V11.667L14 4.667l9.333 7v14" stroke="#F54900" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="10.5" y="17.5" width="7" height="8.167" rx="1" stroke="#F54900" strokeWidth="2"/>
      </svg>
    ),
    image: "/assets/skilled-visas-australia/in-demand-industries/construction.jpg",
  },
  {
    label: "Education",
    desc: "Teachers and education administrators are in high demand across schools and universities.",
    bg: "bg-yellow-100",
    iconColor: "#CA8A04",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 4.667L2.333 10.5 14 16.333l11.667-5.833L14 4.667z" stroke="#CA8A04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7 13.417v5.25C7 21 10.134 23.333 14 23.333s7-2.333 7-4.666v-5.25" stroke="#CA8A04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    image: "/assets/skilled-visas-australia/in-demand-industries/education.jpg",
  },
  {
    label: "Skilled Trades",
    desc: "Electricians, plumbers, and welders are consistently among Australia's most wanted occupations.",
    bg: "bg-pink-100",
    iconColor: "#DB2777",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.25 22.75L16.042 11.958m0 0a4.375 4.375 0 1 0 6.187-6.187 4.375 4.375 0 0 0-6.187 6.187zm-9.334 5.834L4.083 15.75l2.334-2.333 5.833 1.166" stroke="#DB2777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    image: "/assets/skilled-visas-australia/in-demand-industries/skilled-trades.jpg",
  },
];

const faqs = [
  {
    q: "Who can apply for a Skilled Visa in Australia?",
    a: "Professionals with qualifications and work experience in occupations listed on the Australian Skilled Occupation Lists may be eligible to apply."
  },
  {
    q: "What is the minimum points requirement for Skilled Migration?",
    a: "Most Australian Skilled Visas require a minimum of 65 migration points, but higher scores improve the chances of receiving an invitation."
  },
  {
    q: "Can Skilled Visas lead to Permanent Residency?",
    a: "Yes. Many Skilled Visas Australia either grant permanent residency directly or provide a pathway to permanent residency."
  },
  {
    q: "Can family members be included in the application?",
    a: "Yes. Most Australian Skilled Visas allow applicants to include their partner and dependent children."
  }
];

const processSteps = [
  {
    step: "01",
    title: "Skills Assessment",
    desc: "Applicants must obtain a positive skills assessment from the relevant assessing authority for their nominated occupation.",
    image: "/assets/skilled-visas-australia/application-process/step-01.jpg",
  },
  {
    step: "02",
    title: "English Language Test",
    desc: "Applicants must demonstrate English proficiency through approved tests, such as IELTS or PTE.",
    image: "/assets/skilled-visas-australia/application-process/step-02.jpg",
  },
  {
    step: "03",
    title: "Expression of Interest",
    desc: "Applicants submit an Expression of Interest through SkillSelect, Australia's online immigration system.",
    image: "/assets/skilled-visas-australia/application-process/step-03.jpg",
  },
  {
    step: "04",
    title: "Invitation to Apply",
    desc: "Candidates with competitive points scores may receive an invitation to apply for a skilled visa.",
    image: "/assets/skilled-visas-australia/application-process/step-04.jpg",
  },
  {
    step: "05",
    title: "Visa Application",
    desc: "Applicants submit their complete visa application with all required supporting documents.",
    image: "/assets/skilled-visas-australia/application-process/step-05.jpg",
  },
  {
    step: "06",
    title: "Visa Decision",
    desc: "The Department of Home Affairs reviews the application and decides whether to grant the visa.",
    image: "/assets/skilled-visas-australia/application-process/step-06.jpg",
  },
];

export default function SkilledVisasAustraliaPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [activeImage, setActiveImage] = useState(industries[0].image);
  const [activeProcess, setActiveProcess] = useState(0);
  const [prevProcess, setPrevProcess] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<'next' | 'prev'>('next');
  const [imgAnimating, setImgAnimating] = useState(false);

  const navigateProcess = (dir: 'next' | 'prev') => {
    if (imgAnimating) return;
    const nextIdx = dir === 'next'
      ? (activeProcess + 1) % processSteps.length
      : (activeProcess - 1 + processSteps.length) % processSteps.length;
    setSlideDir(dir);
    setPrevProcess(activeProcess);
    setActiveProcess(nextIdx);
    setImgAnimating(true);
    setTimeout(() => { setPrevProcess(null); setImgAnimating(false); }, 540);
  };

  const goToProcess = (idx: number) => {
    if (idx === activeProcess || imgAnimating) return;
    const dir = idx > activeProcess ? 'next' : 'prev';
    setSlideDir(dir);
    setPrevProcess(activeProcess);
    setActiveProcess(idx);
    setImgAnimating(true);
    setTimeout(() => { setPrevProcess(null); setImgAnimating(false); }, 540);
  };

  // Auto-slide every 3 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      navigateProcess('next');
    }, 3000);
    return () => clearInterval(timer);
  }, [activeProcess, imgAnimating]);

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];

  const onLinkClick = useNavLinkClick(links);
  const navigate = useNavigate();

  useSeo({
    title: "Skilled Independent Visa Subclass 189 Australia | PR Visa",
    description: "Apply for Skilled Independent Visa Subclass 189 and get PR in Australia without sponsorship. Check eligibility, points, and start your EOI today.",
    path: "/australia-skilled-independent-visa-189",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Skilled Independent Visa 189", url: "/australia-skilled-independent-visa-189" }
    ],
    faqItems: [
      { question: "What is Skilled Independent Visa Subclass 189?", answer: "A points-based PR visa for skilled workers. No sponsorship needed." },
      { question: "Is Subclass 189 a permanent visa?", answer: "Yes, it provides direct permanent residency." },
      { question: "Do I need sponsorship for Subclass 189?", answer: "No, it does not require employer or state sponsorship." },
      { question: "How many points are required?", answer: "Minimum 65 points, but higher scores increase chances." }
    ]
  });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Logo" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>
      <main className="w-full flex flex-col items-center max-w-full">
        {/* HERO SECTION */}
        <section className="bg-gradient-to-b from-[#F5F7FF] via-[#fffbee] to-[#E6EFFF] w-full font-poppins">
          <main className="flex flex-col md:flex-row items-center max-md:text-center justify-between mt-24 pb-16 px-6 sm:px-10 md:px-24 max-w-7xl mx-auto w-full">

            {/* Left: Text Content */}
            <div className="flex flex-col items-center md:items-start">

              {/* Badge pill */}
              <button
                type="button"
                className="mt-16 mb-6 flex items-center space-x-2 border border-indigo-600 text-indigo-600 text-xs rounded-full px-4 py-1.5 hover:bg-indigo-50 transition"
              >
                <span>Explore Australia's skilled migration pathways.</span>
              </button>

              {/* Heading */}
              <h1 className="text-gray-900 font-semibold text-4xl sm:text-5xl md:text-6xl max-w-xl">
                Skilled Visas{" "}
                <span className="text-indigo-600">Australia</span>
              </h1>

              {/* Description */}
              <p className="mt-4 text-gray-600 max-w-md text-sm sm:text-base leading-relaxed">
                Build your future in Australia with in-demand skills, PR pathways, and top career opportunities.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col md:flex-row items-center mt-8 gap-3">
                <button
                  type="button"
                  onClick={() => { navigate("/australia-migration-eligibility-checker"); }}
                  className="bg-indigo-600 text-white px-6 pr-2.5 py-2.5 rounded-full text-sm font-medium flex items-center space-x-2 hover:bg-indigo-700 transition"
                >
                  <span>Check My Eligibility</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.821 11.999h13.43m0 0-6.714-6.715m6.715 6.715-6.715 6.715" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => { navigate("/contact?from=skilled-visas-page-cta"); }}
                  className="text-indigo-600 bg-indigo-100 px-5 py-2 rounded-full text-sm font-medium hover:bg-indigo-200 transition"
                >
                  Get a Call
                </button>
              </div>
            </div>

            {/* Right: 2×2 Image Grid */}
            <div aria-label="Skilled visa images" className="mt-12 grid grid-cols-2 gap-6 pb-6">
              <img alt="Skilled Visas" src="/assets/services-cards/images/skilled-visas.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
              <img alt="Skills Assessment" src="/assets/services-cards/images/skills-assessment.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
              <img alt="Employer Sponsored" src="/assets/services-cards/images/employer-sponsored-visas.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
              <img alt="Corporate Immigration" src="/assets/services-cards/images/corporate-immigration.jpg" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover flex-shrink-0 shadow-lg" />
            </div>

          </main>
        </section>

        {/* SECTION 1: WHAT IS SECTION */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16">
            {/* Left: Image */}
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/skilled-visas-australia/what-is-skilled-visa/image.jpg"
                alt="What is an Australian Skilled Visa"
              />
            </div>

            {/* Right: Text */}
            <div className="text-sm text-slate-600 w-full max-w-lg">
              <h2 className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700" style={{fontFamily:'Poppins, sans-serif'}}>
                What Is an Australian Skilled Visa?
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6 sm:mb-8"></div>
              <p className="text-sm sm:text-base text-[#333] leading-[1.7]">
                An Australian Skilled Visa allows qualified professionals to migrate based on their education, work experience, and skills.
                It also considers their potential contribution to the Australian economy.
              </p>
              <p className="mt-4 text-sm sm:text-base text-[#333] leading-[1.7]">
                Most Skilled Migration Visas Australia operate under a points-based immigration system.
                Applicants are assessed and ranked based on several factors that determine their eligibility.
              </p>

            </div>
          </div>
        </section>

        {/* TYPES OF SKILLED VISAS SECTION */}
        <section className="w-full bg-blue-50 py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3" style={{fontFamily:'Poppins, sans-serif'}}>
              Types of Skilled Visas in Australia
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5"></div>
            <p className="text-base text-slate-500 mb-3 max-w-3xl">
              Australia offers several Skilled Visa pathways based on your profession, experience, and migration goals. Some visas grant direct permanent residency, while others provide temporary work rights before permanent migration.
            </p>
            <p className="text-sm text-slate-400 mb-10">Below are the most common Australian Skilled Visas.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {skilledVisaTypes.map((visa, i) => (
                <VisaCard key={i} {...visa} />
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 2: KEY ELIGIBILITY REQUIREMENTS */}
        <section className="w-full bg-gray-50 py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <h2 className="text-4xl sm:text-5xl font-semibold text-center text-[#0a2540] mx-auto" style={{fontFamily:'Poppins, sans-serif'}}>
            Key Eligibility Requirements
          </h2>
          <p className="text-base text-slate-500 text-center mt-3 max-w-lg mx-auto">
            To qualify for an Australian Skilled Visa, applicants must meet the following criteria set by the Department of Home Affairs.
          </p>
          <div className="relative max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 px-0 pt-12">
            {/* background glow */}
            <div className="size-[520px] -top-80 left-1/2 -translate-x-1/2 rounded-full absolute blur-[300px] -z-10 bg-indigo-100 pointer-events-none"></div>
            {skillsRequirements.map((requirement, index) => {
              const cols = 4;
              const col = index % cols;
              const row = Math.floor(index / cols);
              const totalRows = Math.ceil(skillsRequirements.length / cols);
              const isLastRow = row === totalRows - 1;
              const isLastCol = col === cols - 1;
              return (
                <div
                  key={index}
                  style={{transition: 'background 0.2s ease, box-shadow 0.2s ease'}}
                  className={[
                    "group py-8 px-6 cursor-default rounded-xl",
                    "hover:bg-gradient-to-br hover:from-[#e0f0ff] hover:to-[#f0f7ff] hover:shadow-md",
                    !isLastRow ? "border-b border-gray-200" : "",
                    !isLastCol ? "lg:border-r lg:border-gray-200" : "",
                  ].join(" ")}
                >
                  <div className="mt-2 space-y-2">
                    <p className="text-[1.05rem] sm:text-lg text-slate-600 leading-[1.75]">{requirement}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* SECTION 3: IN-DEMAND INDUSTRIES */}
        <section className="w-full bg-white py-16 px-4 sm:px-6 md:px-10 lg:px-16">
          <div className="w-full max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-semibold text-center text-[#0a2540] mb-3" style={{fontFamily:'Poppins, sans-serif'}}>
              In-Demand Industries in Australia
            </h2>
            <p className="text-base text-slate-500 text-center max-w-lg mx-auto mb-12">
              Skilled professionals are highly sought after across the following sectors:
            </p>

            {(() => {
              const left = industries.slice(0, 3);
              const right = industries.slice(3, 6);
              return (
                <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-0">
                  {/* Left 3 */}
                  <div className="space-y-8 lg:space-y-10 w-full max-w-sm lg:pr-16">
                    {left.map((ind, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-5 cursor-pointer group"
                        onMouseEnter={() => setActiveImage(ind.image)}
                      >
                        <div className={`p-4 aspect-square ${ind.bg} rounded-full shrink-0 transition-transform duration-200 group-hover:scale-110`}>
                          {ind.icon}
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-base font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">{ind.label}</h3>
                          <p className="text-sm text-slate-500 leading-relaxed">{ind.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Centre Image */}
                  <div className="hidden lg:block shrink-0 w-full max-w-xs sm:max-w-sm lg:max-w-xs xl:max-w-sm">
                    <div className="relative rounded-2xl overflow-hidden aspect-square shadow-xl shadow-indigo-200/60">
                      <img
                        key={activeImage}
                        src={activeImage}
                        alt="Industry"
                        className="w-full h-full object-cover transition-opacity duration-500 opacity-100"
                        onError={(e) => { (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1531497865144-0464ef8fb9a9?q=80&w=451&h=451&auto=format&fit=crop"; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/30 to-transparent rounded-2xl pointer-events-none" />
                    </div>
                  </div>

                  {/* Right 3 */}
                  <div className="space-y-8 lg:space-y-10 w-full max-w-sm lg:pl-16">
                    {right.map((ind, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-5 cursor-pointer group"
                        onMouseEnter={() => setActiveImage(ind.image)}
                      >
                        <div className={`p-4 aspect-square ${ind.bg} rounded-full shrink-0 transition-transform duration-200 group-hover:scale-110`}>
                          {ind.icon}
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-base font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">{ind.label}</h3>
                          <p className="text-sm text-slate-500 leading-relaxed">{ind.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* SECTION 4: SKILLED VISA APPLICATION PROCESS */}
        <section className="w-full relative overflow-hidden" style={{ background: '#1B31F5' }}>
          <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 pt-16 pb-0 relative">
            {/* Large background step number — fades up on change */}
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-end pr-4 sm:pr-20 pointer-events-none select-none z-0 overflow-hidden"
            >
              <span
                key={`num-${activeProcess}`}
                className="animate-process-num text-[320px] sm:text-[420px] md:text-[520px] font-black leading-none"
                style={{ color: 'rgba(255,255,255,0.13)', letterSpacing: '-0.04em' }}
              >
                {processSteps[activeProcess].step}
              </span>
            </div>

            {/* Centred section title — single line */}
            <h2
              className="relative z-10 text-white font-bold text-3xl sm:text-4xl md:text-5xl text-center whitespace-nowrap mb-12"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Skilled Visa Application Process
            </h2>

            {/* Main content: left = step info + arrows, right = sliding image */}
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">

              {/* Left: step label, title, description, arrows */}
              <div
                key={activeProcess}
                className={`flex-1 max-w-lg ${
                  prevProcess !== null
                    ? slideDir === 'next' ? 'proc-step-enter-right' : 'proc-step-enter-left'
                    : ''
                }`}
              >
                <p className="text-white/50 text-xs uppercase tracking-[0.2em] font-semibold mb-3">
                  Step {processSteps[activeProcess].step}
                </p>
                <h3 className="text-white font-bold text-3xl sm:text-4xl mb-4 leading-snug">
                  {processSteps[activeProcess].title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-10">
                  {processSteps[activeProcess].desc}
                </p>
                {/* Navigation arrows */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigateProcess('prev')}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Previous step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigateProcess('next')}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Next step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Right: directional slide+tilt image — free movement */}
              <div className="flex-shrink-0 flex items-end justify-center lg:justify-end mb-14">
                <div className="relative w-80 h-64 sm:w-[440px] sm:h-80">
                  {/* Exiting image */}
                  {prevProcess !== null && (
                    <img
                      src={processSteps[prevProcess].image}
                      alt=""
                      aria-hidden="true"
                      className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${
                        slideDir === 'next' ? 'proc-img-exit-left' : 'proc-img-exit-right'
                      }`}
                    />
                  )}
                  {/* Entering image */}
                  <img
                    key={activeProcess}
                    src={processSteps[activeProcess].image}
                    alt={processSteps[activeProcess].title}
                    className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${
                      prevProcess !== null
                        ? slideDir === 'next' ? 'proc-img-enter-right' : 'proc-img-enter-left'
                        : ''
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 md:px-16">
            {/* Steps row — line is drawn behind, dots sit on it */}
            <div className="flex items-start gap-0 overflow-x-auto pb-12 scrollbar-hide">
              {processSteps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goToProcess(i)}
                  className={`flex flex-col items-start flex-1 min-w-[90px] pr-3 transition-opacity duration-300 ${
                    activeProcess === i ? 'opacity-100' : 'opacity-40 hover:opacity-65'
                  }`}
                >
                  {/* Dot + line row */}
                  <div className="relative w-full flex items-center mb-4" style={{ height: '14px' }}>
                    {/* Line segment (full width, sits at vertical center) */}
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-white/25" />
                    {/* Dot on the line */}
                    <span className={`relative z-10 block rounded-full border-2 transition-all duration-300 shrink-0 ${
                      activeProcess === i
                        ? 'w-3.5 h-3.5 bg-white border-white shadow-[0_0_8px_2px_rgba(255,255,255,0.45)]'
                        : 'w-2 h-2 bg-white/30 border-white/40'
                    }`} />
                  </div>
                  <span className={`font-bold text-base sm:text-lg leading-none ${
                    activeProcess === i ? 'text-white' : 'text-white/70'
                  }`}>
                    {s.step}
                  </span>
                  <span className={`text-xs mt-1.5 leading-snug text-left ${
                    activeProcess === i ? 'text-white font-semibold' : 'text-white/55'
                  }`}>
                    {s.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5: BENEFITS OF SKILLED MIGRATION */}
        <section className="w-full bg-zinc-100 py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <h2 className="text-4xl md:text-5xl font-semibold text-neutral-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Benefits of Skilled Migration to Australia
              </h2>
              <p className="text-base text-neutral-600 max-w-lg mt-2">
                Migrating to Australia through the Skilled Migration Program offers significant advantages for professionals and their families.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[320px_1fr_1fr] gap-6">

              {/* Left indigo card — spans 2 rows — first benefit */}
              <div className="md:row-span-2 relative rounded-2xl overflow-hidden bg-indigo-600 p-8 flex flex-col justify-between min-h-[360px]">
                <div aria-hidden="true" className="absolute -bottom-4 -right-3 text-[150px] font-black leading-none text-white/10 select-none pointer-events-none">AU</div>
                <div>
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                    {/* Globe / world icon */}
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.8"/>
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="white" strokeWidth="1.8"/>
                    </svg>
                  </div>
                  <h3 className="text-white font-semibold text-2xl mb-3 leading-snug">Opportunity to live and work in Australia</h3>
                  <p className="text-white/70 text-sm leading-relaxed mt-3">
                    Australia provides excellent long-term career and lifestyle opportunities for skilled professionals.
                  </p>
                </div>
              </div>

              {/* Benefits 2–5 as white cards with unique icons */}
              {[
                {
                  text: "Pathway to Australian Permanent Residency",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 22V12h6v10" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
                {
                  text: "Access to high-quality healthcare and education",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
                {
                  text: "Strong employment opportunities in a stable economy",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="7" width="20" height="14" rx="2" stroke="#4F46E5" strokeWidth="1.8"/>
                      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round"/>
                      <path d="M12 12v4M10 14h4" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  ),
                },
                {
                  text: "Pathway to Australian Citizenship",
                  icon: (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 12l2 2 4-4" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
              ].map((benefit, i) => (
                <div key={i} className="bg-white rounded-2xl hover:shadow-md transition-all duration-300 p-7 flex flex-col justify-between min-h-[160px]">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-5">
                      {benefit.icon}
                    </div>
                    <p className="text-base font-medium text-neutral-700 leading-[1.6]">{benefit.text}</p>
                  </div>
                </div>
              ))}

            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="w-full bg-gray-50 py-24 px-4">
          <div className="w-full max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Header Side */}
              <div className="lg:sticky lg:top-24">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Self Service Help</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-[#0a2540] mb-8" style={{lineHeight: '1.1'}}>
                  Frequently Asked <br /> Questions
                </h2>
                <p className="text-base text-gray-600 max-w-sm mb-6 leading-relaxed">
                  Have questions about Skilled Visas? Find answers to commonly asked questions below.
                </p>
                {/* FAQ image */}
                <div className="rounded-2xl overflow-hidden w-full max-w-sm">
                  <img
                    src="/assets/skilled-visas-australia/faq/faq-image.png"
                    alt="Skilled Visa FAQ"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>

              {/* FAQ Items Side */}
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-white hover:border-blue-300 transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? -1 : i)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-base sm:text-lg font-semibold text-[#0a2540] pr-4">{faq.q}</span>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100">
                        {openFaqIndex === i ? (
                          <Minus className="w-4 h-4 text-gray-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                    </button>
                    {openFaqIndex === i && (
                      <div className="px-6 pb-6">
                        <div className="h-px bg-gray-200 mb-6" />
                        <p className="text-base text-gray-700 leading-relaxed">
                          {faq.a}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <CTA02 />
      </main>
      <Footer />
    </div>
  );
}
